// Alerts hub — one page where a user controls what notifications they
// get: location-based "events near me" + the lists of orgs/vendors they
// follow. Follow management lives on the respective public profile
// pages; this page is for configuring the city alert and seeing a
// summary of what they're subscribed to.
import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Bell, MapPin, Loader2, Users2, Store, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function AlertsSettings() {
  const { user, loading: authLoading } = useAuth();
  const [prefs, setPrefs] = useState({ alert_city: "", alert_city_enabled: false, profile_city: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [attending, setAttending] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, v, a] = await Promise.all([
        api.get("/me/location-alerts"),
        api.get("/follows/vendors").catch(() => ({ data: { items: [] } })),
        api.get("/follows/attending-events").catch(() => ({ data: { items: [] } })),
      ]);
      setPrefs(p.data);
      setVendors(v.data.items || []);
      setAttending(a.data.items || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/alerts" replace />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/location-alerts", {
        alert_city: prefs.alert_city || "",
        alert_city_enabled: !!prefs.alert_city_enabled,
      });
      setPrefs(data);
      toast.success(
        data.alert_city_enabled
          ? `Alerts on for events in ${data.alert_city || "your area"}`
          : "Nearby-event alerts turned off"
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setSaving(false);
    }
  };

  const useProfileCity = () => {
    if (prefs.profile_city) {
      setPrefs((p) => ({ ...p, alert_city: p.profile_city }));
    }
  };

  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12"
      data-testid="alerts-page"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            backgroundColor: "rgba(201,67,42,0.10)",
            color: "var(--terracotta)",
          }}
        >
          <Bell size={22} />
        </div>
        <div className="flex-1">
          <div
            className="text-[10px] uppercase tracking-widest"
            style={{ color: "var(--terracotta)" }}
          >
            Alerts
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            Stay in the loop
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
            Hear about events near you, and every time a vendor or organizer
            you follow has something new.
          </p>
        </div>
      </div>

      {/* ── Near-me alerts ───────────────────────────── */}
      <div
        className="mt-8 rounded-lg border p-5 space-y-4"
        data-testid="near-me-card"
        style={{ borderColor: "rgba(0,0,0,0.08)", backgroundColor: "#fff" }}
      >
        <div className="flex items-start gap-3">
          <MapPin size={18} style={{ color: "var(--avt-purple)" }} />
          <div className="flex-1">
            <div className="font-serif text-xl">Events near me</div>
            <p
              className="text-sm mt-1"
              style={{ color: "var(--ink-soft)" }}
            >
              We'll ping you in the app the moment a new event is posted in
              your city.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="alert-city-enabled"
            data-testid="alert-city-enabled"
            checked={!!prefs.alert_city_enabled}
            onCheckedChange={(v) =>
              setPrefs((p) => ({ ...p, alert_city_enabled: Boolean(v) }))
            }
            disabled={saving || loading}
          />
          <Label
            htmlFor="alert-city-enabled"
            className="text-sm cursor-pointer"
          >
            Alert me about events in a specific city
          </Label>
        </div>

        {prefs.alert_city_enabled && (
          <div className="space-y-2 pl-7">
            <Label htmlFor="alert-city" className="text-xs uppercase tracking-widest">
              City
            </Label>
            <div className="flex gap-2">
              <Input
                id="alert-city"
                data-testid="alert-city-input"
                value={prefs.alert_city || ""}
                onChange={(e) =>
                  setPrefs((p) => ({ ...p, alert_city: e.target.value }))
                }
                placeholder="e.g. Portland, OR"
                maxLength={120}
                disabled={saving || loading}
              />
              {prefs.profile_city && prefs.profile_city !== prefs.alert_city && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={useProfileCity}
                  data-testid="alert-use-profile-city"
                  disabled={saving || loading}
                  className="whitespace-nowrap"
                >
                  Use {prefs.profile_city}
                </Button>
              )}
            </div>
            <p
              className="text-[11px]"
              style={{ color: "var(--ink-soft)" }}
            >
              Match is case-insensitive and matches the exact city text on
              new events — try to use the same formatting as event listings.
            </p>
          </div>
        )}

        <div className="pt-2 flex justify-end">
          <Button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="alert-save-btn"
            style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
            className="rounded-full"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Saving…
              </>
            ) : (
              "Save preferences"
            )}
          </Button>
        </div>
      </div>

      {/* ── Followed vendors ────────────────────────── */}
      <div
        className="mt-6 rounded-lg border p-5"
        data-testid="followed-vendors-card"
        style={{ borderColor: "rgba(0,0,0,0.08)", backgroundColor: "#fff" }}
      >
        <div className="flex items-start gap-3">
          <Store size={18} style={{ color: "var(--avt-purple)" }} />
          <div className="flex-1">
            <div className="font-serif text-xl">Vendors you follow</div>
            <p
              className="text-sm mt-1"
              style={{ color: "var(--ink-soft)" }}
            >
              You'll hear every time one of these makers gets approved to a
              new event.
            </p>
          </div>
        </div>

        {loading ? (
          <div
            className="py-6 text-center text-sm"
            style={{ color: "var(--ink-soft)" }}
          >
            <Loader2 size={14} className="animate-spin inline mr-2" />
            Loading…
          </div>
        ) : vendors.length === 0 ? (
          <div
            className="py-6 text-center text-sm space-y-1"
            data-testid="followed-vendors-empty"
            style={{ color: "var(--ink-soft)" }}
          >
            <p>You're not following any vendors yet.</p>
            <Link
              to="/vendors"
              className="underline"
              style={{ color: "var(--terracotta)" }}
            >
              Browse the directory →
            </Link>
          </div>
        ) : (
          <ul className="mt-4 divide-y" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
            {vendors.map((v) => (
              <li key={v.id} className="py-3 flex items-center gap-3">
                {v.avatar_url ? (
                  <img
                    src={v.avatar_url}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-serif"
                    style={{
                      backgroundColor: "var(--sand)",
                      color: "var(--terracotta)",
                    }}
                  >
                    {(v.business_name || v.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/vendor/${v.slug || v.id}`}
                    className="font-medium truncate block hover:underline"
                  >
                    {v.business_name || v.name}
                  </Link>
                  {v.city && (
                    <div
                      className="text-xs truncate"
                      style={{ color: "var(--ink-soft)" }}
                    >
                      {v.city}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {attending.length > 0 && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <div
              className="text-[11px] uppercase tracking-widest mb-2 inline-flex items-center gap-1"
              style={{ color: "var(--terracotta)" }}
            >
              <Calendar size={11} /> Where they'll be next
            </div>
            <ul className="space-y-1.5">
              {attending.slice(0, 5).map((e) => (
                <li key={e.id}>
                  <Link
                    to={`/events/${e.id}`}
                    className="text-sm hover:underline"
                  >
                    <strong>{e.title}</strong>
                    <span style={{ color: "var(--ink-soft)" }}>
                      {" · "}
                      {e.date}
                      {e.city ? ` · ${e.city}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ── Followed organizers link ────────────────── */}
      <div
        className="mt-6 rounded-lg border p-5"
        data-testid="followed-organizers-card"
        style={{ borderColor: "rgba(0,0,0,0.08)", backgroundColor: "#fff" }}
      >
        <div className="flex items-start gap-3">
          <Users2 size={18} style={{ color: "var(--avt-purple)" }} />
          <div className="flex-1">
            <div className="font-serif text-xl">Organizers you follow</div>
            <p
              className="text-sm mt-1"
              style={{ color: "var(--ink-soft)" }}
            >
              Every time an organizer you follow publishes a new event, it
              lands in your bell.
            </p>
            {user.role === "vendor" && (
              <Link
                to="/vendor/favorites"
                className="text-sm underline mt-2 inline-block"
                style={{ color: "var(--terracotta)" }}
              >
                Manage your organizer list →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
